export type LegacyMarkerStatus = 'optimal' | 'watch' | 'attention' | 'unknown';
export type MarkerBand =
  | 'optimal'
  | 'slightly_low'
  | 'slightly_high'
  | 'low'
  | 'high'
  | 'critical'
  | 'reference_not_available';

export type IntervalSource = 'lab_provided' | 'fallback_profiled' | 'fallback_generic' | 'unavailable';

export type MethodMatch = 'matched' | 'mismatch' | 'not_provided';

export interface MarkerEvaluationContext {
  sex?: string | number | null;
  age?: number | null;
  pregnancyStatus?: string | boolean | null;
  menopausalStatus?: string | boolean | null;
  fastingState?: string | boolean | null;
  medications?: string[] | string | null;
  assayMethod?: string | null;
  unit?: string | null;
  units?: string | null;
  labUnit?: string | null;
  labRange?: unknown;
  referenceRange?: unknown;
  refRange?: unknown;
  range?: unknown;
  referenceInterval?: unknown;
  low?: number | null;
  high?: number | null;
  min?: number | null;
  max?: number | null;
  marker?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export interface MarkerConfigLike {
  units?: string;
  optimalRange?: string;
}

export interface MarkerRangeSummary {
  lower: number | null;
  upper: number | null;
  unit: string | null;
  source: IntervalSource;
  method: string | null;
}

export interface MarkerEvaluationConfidence {
  intervalSource: IntervalSource;
  unitConversionConfidence: number;
  methodMatch: MethodMatch;
  methodMatchConfidence: number;
  contextCoverage: number;
  overall: number;
  notes: string[];
}

export interface MarkerEvaluationResult {
  status: LegacyMarkerStatus;
  band: MarkerBand;
  direction: 'low' | 'within_range' | 'high' | 'unknown';
  score: number;
  reference: MarkerRangeSummary;
  optimal: MarkerRangeSummary;
  confidence: MarkerEvaluationConfidence;
}

type CanonicalSex = 'male' | 'female' | 'unknown';
type PregnancyState = 'pregnant' | 'not_pregnant' | 'unknown' | 'trimester_1' | 'trimester_2' | 'trimester_3';
type MenopauseState = 'premenopausal' | 'perimenopausal' | 'postmenopausal' | 'unknown';
type FastingState = 'fasting' | 'non_fasting' | 'unknown';

interface IntervalBounds {
  low?: number;
  high?: number;
  unit?: string;
  method?: string;
}

interface NormalizedContext {
  sex: CanonicalSex;
  age: number | null;
  pregnancy: PregnancyState;
  menopause: MenopauseState;
  fasting: FastingState;
  medications: string[];
  assayMethod: string | null;
  measuredUnit: string | null;
  raw: MarkerEvaluationContext;
}

interface ParseRangeResult {
  low?: number;
  high?: number;
  unit?: string;
  method?: string;
}

const MARKER_ALIASES: Record<string, string> = {
  a1c: 'hba1c',
  hscrp: 'hs_crp',
  crp: 'hs_crp',
  ldl_c: 'ldl',
  hdl_c: 'hdl',
  tg: 'triglycerides',
  trig: 'triglycerides',
  vitamin_d_25_oh: 'vitamin_d',
  testosterone: 'testosterone_total',
  glucose_fasting: 'fasting_glucose',
  insulin_fasting: 'fasting_insulin',
  thyroid_tsh: 'tsh'
};

const MARKER_UNIT_FAMILY: Record<string, string> = {
  glucose: 'glucose',
  fasting_glucose: 'glucose',
  glucose_sd: 'glucose',
  ldl: 'cholesterol',
  hdl: 'cholesterol',
  total_cholesterol: 'cholesterol',
  non_hdl: 'cholesterol',
  vldl: 'cholesterol',
  remnant_cholesterol: 'cholesterol',
  triglycerides: 'triglycerides',
  creatinine: 'creatinine',
  vitamin_d: 'vitamin_d',
  testosterone_total: 'testosterone_total',
  testosterone_free: 'testosterone_free',
  c_peptide: 'c_peptide',
  free_t4: 'free_t4',
  total_t4: 'free_t4',
  free_t3: 'free_t3',
  total_t3: 'free_t3'
};

const FAMILY_CONVERSIONS: Record<string, Array<{ from: string; to: string; factor: number }>> = {
  glucose: [
    { from: 'mg/dl', to: 'mmol/l', factor: 1 / 18 },
    { from: 'mmol/l', to: 'mg/dl', factor: 18 }
  ],
  cholesterol: [
    { from: 'mg/dl', to: 'mmol/l', factor: 1 / 38.67 },
    { from: 'mmol/l', to: 'mg/dl', factor: 38.67 }
  ],
  triglycerides: [
    { from: 'mg/dl', to: 'mmol/l', factor: 1 / 88.57 },
    { from: 'mmol/l', to: 'mg/dl', factor: 88.57 }
  ],
  creatinine: [
    { from: 'mg/dl', to: 'umol/l', factor: 88.4 },
    { from: 'umol/l', to: 'mg/dl', factor: 1 / 88.4 }
  ],
  vitamin_d: [
    { from: 'ng/ml', to: 'nmol/l', factor: 2.496 },
    { from: 'nmol/l', to: 'ng/ml', factor: 1 / 2.496 }
  ],
  testosterone_total: [
    { from: 'ng/dl', to: 'nmol/l', factor: 0.0347 },
    { from: 'nmol/l', to: 'ng/dl', factor: 1 / 0.0347 }
  ],
  testosterone_free: [
    { from: 'pg/ml', to: 'pmol/l', factor: 3.467 },
    { from: 'pmol/l', to: 'pg/ml', factor: 1 / 3.467 }
  ],
  c_peptide: [
    { from: 'ng/ml', to: 'nmol/l', factor: 0.331 },
    { from: 'nmol/l', to: 'ng/ml', factor: 1 / 0.331 }
  ],
  free_t4: [
    { from: 'ng/dl', to: 'pmol/l', factor: 12.87 },
    { from: 'pmol/l', to: 'ng/dl', factor: 1 / 12.87 }
  ],
  free_t3: [
    { from: 'pg/ml', to: 'pmol/l', factor: 1.536 },
    { from: 'pmol/l', to: 'pg/ml', factor: 1 / 1.536 }
  ]
};

const MARKER_CONTEXT_RELEVANCE: Record<string, Array<'sex' | 'age' | 'pregnancy' | 'menopause' | 'fasting' | 'medications' | 'assayMethod'>> = {
  hemoglobin: ['sex', 'pregnancy'],
  hematocrit: ['sex', 'pregnancy'],
  rbc: ['sex', 'pregnancy'],
  creatinine: ['sex'],
  ferritin: ['sex', 'menopause', 'pregnancy'],
  testosterone_total: ['sex', 'age', 'menopause'],
  estradiol: ['sex', 'pregnancy', 'menopause'],
  fsh: ['sex', 'menopause'],
  tsh: ['pregnancy', 'assayMethod'],
  fasting_glucose: ['fasting', 'pregnancy', 'medications'],
  glucose: ['fasting', 'pregnancy', 'medications'],
  fasting_insulin: ['fasting', 'pregnancy', 'medications'],
  insulin: ['fasting', 'pregnancy', 'medications'],
  triglycerides: ['fasting', 'medications'],
  psa: ['sex', 'age'],
  hs_crp: ['assayMethod'],
  hba1c: ['pregnancy', 'medications']
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const toNullableNumber = (value: unknown): number | null => {
  if (isFiniteNumber(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.trim().replace(/,/g, '');
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeUnit = (unit: unknown): string | null => {
  if (!unit || typeof unit !== 'string') return null;
  const normalized = unit
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/μ/g, 'u')
    .replace(/µ/g, 'u')
    .replace(/²/g, '2')
    .replace(/³/g, '3');

  const aliases: Record<string, string> = {
    mgdl: 'mg/dl',
    'mg/dl': 'mg/dl',
    mmoll: 'mmol/l',
    'mmol/l': 'mmol/l',
    ngml: 'ng/ml',
    'ng/ml': 'ng/ml',
    pgml: 'pg/ml',
    'pg/ml': 'pg/ml',
    nmoll: 'nmol/l',
    'nmol/l': 'nmol/l',
    pmoll: 'pmol/l',
    'pmol/l': 'pmol/l',
    umoll: 'umol/l',
    'umol/l': 'umol/l',
    ul: 'u/l',
    'u/l': 'u/l',
    iuul: 'iu/l',
    'iu/l': 'iu/l',
    uiul: 'uiu/ml',
    'uiu/ml': 'uiu/ml',
    miul: 'miu/l',
    'miu/l': 'miu/l',
    mlmin173m2: 'ml/min/1.73m2',
    'ml/min/1.73m2': 'ml/min/1.73m2',
    '%': '%'
  };

  return aliases[normalized] || normalized;
};

const canonicalizeMarkerKey = (key?: string): string => {
  const clean = String(key || '').trim().toLowerCase();
  return MARKER_ALIASES[clean] || clean;
};

const parseSex = (value: unknown): CanonicalSex => {
  const raw = String(value || '').trim().toLowerCase();
  if (['male', 'm', 'man'].includes(raw)) return 'male';
  if (['female', 'f', 'woman'].includes(raw)) return 'female';
  return 'unknown';
};

const parsePregnancy = (value: unknown): PregnancyState => {
  if (typeof value === 'boolean') return value ? 'pregnant' : 'not_pregnant';
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'unknown';
  if (raw.includes('trimester 1') || raw === '1' || raw === 't1') return 'trimester_1';
  if (raw.includes('trimester 2') || raw === '2' || raw === 't2') return 'trimester_2';
  if (raw.includes('trimester 3') || raw === '3' || raw === 't3') return 'trimester_3';
  if (['yes', 'pregnant', 'true'].includes(raw)) return 'pregnant';
  if (['no', 'not_pregnant', 'false'].includes(raw)) return 'not_pregnant';
  return 'unknown';
};

const parseMenopause = (value: unknown): MenopauseState => {
  if (typeof value === 'boolean') return value ? 'postmenopausal' : 'premenopausal';
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'unknown';
  if (raw.includes('post')) return 'postmenopausal';
  if (raw.includes('peri')) return 'perimenopausal';
  if (raw.includes('pre')) return 'premenopausal';
  return 'unknown';
};

const parseFasting = (value: unknown): FastingState => {
  if (typeof value === 'boolean') return value ? 'fasting' : 'non_fasting';
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'unknown';
  if (['fasting', 'fasted', 'yes', 'true'].includes(raw)) return 'fasting';
  if (['nonfasting', 'non-fasting', 'fed', 'postprandial', 'no', 'false'].includes(raw)) return 'non_fasting';
  return 'unknown';
};

const normalizeMedicationList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(item => String(item || '').toLowerCase().trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[;,|]/)
      .map(item => item.toLowerCase().trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeContext = (
  sexOrContext: string | number | null | MarkerEvaluationContext | undefined,
  context: MarkerEvaluationContext | undefined,
  markerConfig: MarkerConfigLike | undefined
): NormalizedContext => {
  const base: MarkerEvaluationContext =
    sexOrContext && typeof sexOrContext === 'object'
      ? { ...sexOrContext, ...(context || {}) }
      : { ...(context || {}), sex: context?.sex ?? sexOrContext };

  const markerObj = (base.marker && typeof base.marker === 'object') ? base.marker : null;

  const age = toNullableNumber(base.age ?? markerObj?.age ?? null);
  const assayMethod = String(base.assayMethod ?? markerObj?.assayMethod ?? markerObj?.method ?? '').trim() || null;

  return {
    sex: parseSex(base.sex ?? markerObj?.sex ?? null),
    age,
    pregnancy: parsePregnancy(base.pregnancyStatus ?? markerObj?.pregnancyStatus ?? markerObj?.pregnancy ?? null),
    menopause: parseMenopause(base.menopausalStatus ?? markerObj?.menopausalStatus ?? markerObj?.menopause ?? null),
    fasting: parseFasting(base.fastingState ?? markerObj?.fastingState ?? markerObj?.fasting ?? null),
    medications: normalizeMedicationList(base.medications ?? markerObj?.medications ?? null),
    assayMethod,
    measuredUnit: normalizeUnit(base.unit ?? base.units ?? base.labUnit ?? markerObj?.unit ?? markerObj?.units ?? markerConfig?.units ?? null),
    raw: base
  };
};

const parseRangeFromText = (value: string): ParseRangeResult | null => {
  const source = value.trim();
  if (!source) return null;

  const text = source.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ');

  const lessMatch = text.match(/(?:<=|<)\s*([+-]?\d*\.?\d+)/);
  if (lessMatch) {
    return { high: Number(lessMatch[1]) };
  }

  const greaterMatch = text.match(/(?:>=|>)\s*([+-]?\d*\.?\d+)/);
  if (greaterMatch) {
    return { low: Number(greaterMatch[1]) };
  }

  const rangeMatch = text.match(/([+-]?\d*\.?\d+)\s*(?:-|to|–|—)\s*([+-]?\d*\.?\d+)/);
  if (rangeMatch) {
    const low = Number(rangeMatch[1]);
    const high = Number(rangeMatch[2]);
    if (Number.isFinite(low) && Number.isFinite(high)) {
      return { low: Math.min(low, high), high: Math.max(low, high) };
    }
  }

  const numericValues = text.match(/[+-]?\d*\.?\d+/g)?.map(Number).filter(Number.isFinite) || [];
  if (numericValues.length >= 2) {
    const sorted = numericValues.slice(0, 2).sort((a, b) => a - b);
    return { low: sorted[0], high: sorted[1] };
  }

  return null;
};

const parseRangeUnknown = (value: unknown): ParseRangeResult | null => {
  if (!value) return null;
  if (typeof value === 'string') return parseRangeFromText(value);

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;

    const low = toNullableNumber(obj.low ?? obj.lower ?? obj.min ?? obj.referenceMin ?? obj.refLow ?? null);
    const high = toNullableNumber(obj.high ?? obj.upper ?? obj.max ?? obj.referenceMax ?? obj.refHigh ?? null);

    const parsedFromText = typeof obj.range === 'string'
      ? parseRangeFromText(obj.range)
      : typeof obj.referenceRange === 'string'
        ? parseRangeFromText(obj.referenceRange)
        : null;

    const unit = normalizeUnit(obj.unit ?? obj.units ?? obj.referenceUnit ?? null) || undefined;
    const method = String(obj.method ?? obj.assayMethod ?? '').trim() || undefined;

    const resolvedLow = low ?? parsedFromText?.low;
    const resolvedHigh = high ?? parsedFromText?.high;

    if (resolvedLow === null && resolvedHigh === null && !parsedFromText) return null;

    return {
      low: resolvedLow ?? undefined,
      high: resolvedHigh ?? undefined,
      unit,
      method
    };
  }

  return null;
};

const resolveLabRange = (context: NormalizedContext): ParseRangeResult | null => {
  const raw = context.raw;
  const markerObj = (raw.marker && typeof raw.marker === 'object') ? raw.marker : null;

  const candidates: unknown[] = [
    raw.labRange,
    raw.referenceRange,
    raw.refRange,
    raw.range,
    raw.referenceInterval,
    markerObj?.labRange,
    markerObj?.referenceRange,
    markerObj?.refRange,
    markerObj?.range,
    markerObj?.referenceInterval
  ];

  for (const candidate of candidates) {
    const parsed = parseRangeUnknown(candidate);
    if (!parsed) continue;

    if (parsed.low === undefined && parsed.high === undefined) continue;

    const unit = parsed.unit || normalizeUnit(raw.labUnit ?? raw.unit ?? raw.units ?? markerObj?.units ?? markerObj?.unit ?? null) || undefined;
    const method = parsed.method || context.assayMethod || undefined;

    return {
      low: parsed.low,
      high: parsed.high,
      unit,
      method
    };
  }

  const low = toNullableNumber(raw.low ?? raw.min ?? markerObj?.low ?? markerObj?.min ?? null);
  const high = toNullableNumber(raw.high ?? raw.max ?? markerObj?.high ?? markerObj?.max ?? null);
  if (low !== null || high !== null) {
    return {
      low: low ?? undefined,
      high: high ?? undefined,
      unit: normalizeUnit(raw.labUnit ?? raw.unit ?? raw.units ?? markerObj?.unit ?? markerObj?.units ?? null) || undefined,
      method: context.assayMethod || undefined
    };
  }

  return null;
};

const convertValueIfNeeded = (
  markerKey: string,
  value: number,
  fromUnit: string | null,
  toUnit: string | null
): { value: number; confidence: number; note?: string } => {
  if (!fromUnit || !toUnit || fromUnit === toUnit) {
    return { value, confidence: 1 };
  }

  const family = MARKER_UNIT_FAMILY[markerKey];
  if (!family) {
    return {
      value,
      confidence: 0.45,
      note: `No validated conversion map for ${markerKey} (${fromUnit} to ${toUnit}).`
    };
  }

  const conversion = (FAMILY_CONVERSIONS[family] || []).find(rule => rule.from === fromUnit && rule.to === toUnit);
  if (!conversion) {
    return {
      value,
      confidence: 0.45,
      note: `Missing conversion factor for ${markerKey} (${fromUnit} to ${toUnit}).`
    };
  }

  return {
    value: value * conversion.factor,
    confidence: 0.95,
    note: `Converted ${markerKey} from ${fromUnit} to ${toUnit}.`
  };
};

const deriveOptimalFromReference = (reference: IntervalBounds): IntervalBounds => {
  const low = reference.low;
  const high = reference.high;

  if (isFiniteNumber(low) && isFiniteNumber(high) && high > low) {
    const width = high - low;
    const innerLow = low + width * 0.15;
    const innerHigh = high - width * 0.15;
    if (innerHigh > innerLow) {
      return { low: innerLow, high: innerHigh, unit: reference.unit, method: reference.method };
    }
    return { low, high, unit: reference.unit, method: reference.method };
  }

  if (isFiniteNumber(high)) {
    return { high: high * 0.9, unit: reference.unit, method: reference.method };
  }

  if (isFiniteNumber(low)) {
    return { low: low * 1.1, unit: reference.unit, method: reference.method };
  }

  return { unit: reference.unit, method: reference.method };
};

const parseGenericFallback = (optimalRange: string | undefined, unit: string | null): { reference: IntervalBounds; optimal: IntervalBounds } | null => {
  if (!optimalRange) return null;
  const text = optimalRange.trim();
  if (!text || text.toLowerCase() === 'low') return null;

  const parsed = parseRangeFromText(text);
  if (!parsed) return null;

  const reference: IntervalBounds = {
    low: parsed.low,
    high: parsed.high,
    unit: unit || parsed.unit,
    method: parsed.method
  };

  return {
    reference,
    optimal: deriveOptimalFromReference(reference)
  };
};

const adjustForProfiledRules = (markerKey: string, context: NormalizedContext): { reference: IntervalBounds; optimal: IntervalBounds; notes: string[] } | null => {
  const notes: string[] = [];

  switch (markerKey) {
    case 'hemoglobin': {
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy-specific hemoglobin interval applied.');
        return {
          reference: { low: 11.0, high: 14.0, unit: 'g/dl' },
          optimal: { low: 11.5, high: 13.5, unit: 'g/dl' },
          notes
        };
      }
      if (context.sex === 'male') {
        notes.push('Sex-specific male interval applied.');
        return {
          reference: { low: 13.5, high: 17.5, unit: 'g/dl' },
          optimal: { low: 14.0, high: 16.5, unit: 'g/dl' },
          notes
        };
      }
      if (context.sex === 'female') {
        notes.push('Sex-specific female interval applied.');
        return {
          reference: { low: 12.0, high: 15.5, unit: 'g/dl' },
          optimal: { low: 12.5, high: 14.8, unit: 'g/dl' },
          notes
        };
      }
      return {
        reference: { low: 12.0, high: 17.5, unit: 'g/dl' },
        optimal: { low: 13.0, high: 15.8, unit: 'g/dl' },
        notes
      };
    }
    case 'hematocrit': {
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy-specific hematocrit interval applied.');
        return {
          reference: { low: 33, high: 44, unit: '%' },
          optimal: { low: 35, high: 41, unit: '%' },
          notes
        };
      }
      if (context.sex === 'male') {
        notes.push('Sex-specific male interval applied.');
        return {
          reference: { low: 41, high: 53, unit: '%' },
          optimal: { low: 43, high: 50, unit: '%' },
          notes
        };
      }
      if (context.sex === 'female') {
        notes.push('Sex-specific female interval applied.');
        return {
          reference: { low: 36, high: 46, unit: '%' },
          optimal: { low: 38, high: 44, unit: '%' },
          notes
        };
      }
      return {
        reference: { low: 36, high: 53, unit: '%' },
        optimal: { low: 39, high: 48, unit: '%' },
        notes
      };
    }
    case 'rbc': {
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy-specific RBC interval applied.');
        return {
          reference: { low: 3.8, high: 4.8, unit: 'm/ul' },
          optimal: { low: 4.0, high: 4.6, unit: 'm/ul' },
          notes
        };
      }
      if (context.sex === 'male') {
        notes.push('Sex-specific male interval applied.');
        return {
          reference: { low: 4.5, high: 5.9, unit: 'm/ul' },
          optimal: { low: 4.8, high: 5.6, unit: 'm/ul' },
          notes
        };
      }
      if (context.sex === 'female') {
        notes.push('Sex-specific female interval applied.');
        return {
          reference: { low: 4.1, high: 5.1, unit: 'm/ul' },
          optimal: { low: 4.3, high: 4.9, unit: 'm/ul' },
          notes
        };
      }
      return {
        reference: { low: 4.1, high: 5.9, unit: 'm/ul' },
        optimal: { low: 4.4, high: 5.2, unit: 'm/ul' },
        notes
      };
    }
    case 'creatinine': {
      if (context.sex === 'male') {
        notes.push('Sex-specific male creatinine interval applied.');
        return {
          reference: { low: 0.74, high: 1.35, unit: 'mg/dl' },
          optimal: { low: 0.8, high: 1.2, unit: 'mg/dl' },
          notes
        };
      }
      if (context.sex === 'female') {
        notes.push('Sex-specific female creatinine interval applied.');
        return {
          reference: { low: 0.59, high: 1.04, unit: 'mg/dl' },
          optimal: { low: 0.65, high: 0.95, unit: 'mg/dl' },
          notes
        };
      }
      return {
        reference: { low: 0.59, high: 1.35, unit: 'mg/dl' },
        optimal: { low: 0.7, high: 1.1, unit: 'mg/dl' },
        notes
      };
    }
    case 'ferritin': {
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy-specific ferritin floor applied.');
        return {
          reference: { low: 15, high: 150, unit: 'ng/ml' },
          optimal: { low: 30, high: 100, unit: 'ng/ml' },
          notes
        };
      }
      if (context.sex === 'male') {
        notes.push('Sex-specific male ferritin interval applied.');
        return {
          reference: { low: 30, high: 400, unit: 'ng/ml' },
          optimal: { low: 50, high: 200, unit: 'ng/ml' },
          notes
        };
      }
      if (context.sex === 'female') {
        if (context.menopause === 'postmenopausal') {
          notes.push('Postmenopausal ferritin interval applied.');
          return {
            reference: { low: 20, high: 250, unit: 'ng/ml' },
            optimal: { low: 35, high: 150, unit: 'ng/ml' },
            notes
          };
        }
        notes.push('Premenopausal ferritin interval applied.');
        return {
          reference: { low: 15, high: 150, unit: 'ng/ml' },
          optimal: { low: 30, high: 120, unit: 'ng/ml' },
          notes
        };
      }
      return {
        reference: { low: 15, high: 300, unit: 'ng/ml' },
        optimal: { low: 35, high: 160, unit: 'ng/ml' },
        notes
      };
    }
    case 'testosterone_total': {
      if (context.sex === 'male') {
        const age = context.age ?? 40;
        const lower = age >= 60 ? 250 : age >= 45 ? 280 : 300;
        notes.push('Sex and age adjusted male testosterone interval applied.');
        return {
          reference: { low: lower, high: 1000, unit: 'ng/dl' },
          optimal: { low: 450, high: 850, unit: 'ng/dl' },
          notes
        };
      }
      if (context.sex === 'female') {
        notes.push('Sex-specific female testosterone interval applied.');
        return {
          reference: { low: 15, high: 70, unit: 'ng/dl' },
          optimal: { low: 20, high: 45, unit: 'ng/dl' },
          notes
        };
      }
      return {
        reference: { low: 15, high: 1000, unit: 'ng/dl' },
        optimal: { low: 250, high: 800, unit: 'ng/dl' },
        notes
      };
    }
    case 'estradiol': {
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy-specific estradiol interval applied.');
        return {
          reference: { low: 100, high: 10000, unit: 'pg/ml' },
          optimal: { low: 500, high: 6000, unit: 'pg/ml' },
          notes
        };
      }
      if (context.sex === 'female') {
        if (context.menopause === 'postmenopausal') {
          notes.push('Postmenopausal estradiol interval applied.');
          return {
            reference: { low: 5, high: 40, unit: 'pg/ml' },
            optimal: { low: 10, high: 30, unit: 'pg/ml' },
            notes
          };
        }
        notes.push('Premenopausal estradiol interval applied (cycle phase unknown).');
        return {
          reference: { low: 30, high: 400, unit: 'pg/ml' },
          optimal: { low: 60, high: 220, unit: 'pg/ml' },
          notes
        };
      }
      return {
        reference: { low: 10, high: 60, unit: 'pg/ml' },
        optimal: { low: 15, high: 45, unit: 'pg/ml' },
        notes
      };
    }
    case 'fsh': {
      if (context.sex === 'female') {
        if (context.menopause === 'postmenopausal') {
          notes.push('Postmenopausal FSH interval applied.');
          return {
            reference: { low: 25, high: 135, unit: 'miu/l' },
            optimal: { low: 35, high: 90, unit: 'miu/l' },
            notes
          };
        }
        notes.push('Premenopausal FSH interval applied (cycle phase unknown).');
        return {
          reference: { low: 3, high: 20, unit: 'miu/l' },
          optimal: { low: 4, high: 12, unit: 'miu/l' },
          notes
        };
      }
      if (context.sex === 'male') {
        notes.push('Male FSH interval applied.');
        return {
          reference: { low: 1.5, high: 12.4, unit: 'miu/l' },
          optimal: { low: 2.5, high: 8.5, unit: 'miu/l' },
          notes
        };
      }
      return {
        reference: { low: 1.5, high: 20, unit: 'miu/l' },
        optimal: { low: 3, high: 10, unit: 'miu/l' },
        notes
      };
    }
    case 'tsh': {
      if (context.pregnancy === 'trimester_1') {
        notes.push('Pregnancy trimester 1 TSH interval applied.');
        return {
          reference: { low: 0.1, high: 2.5, unit: 'miu/l' },
          optimal: { low: 0.3, high: 2.0, unit: 'miu/l' },
          notes
        };
      }
      if (context.pregnancy === 'trimester_2' || context.pregnancy === 'trimester_3' || context.pregnancy === 'pregnant') {
        notes.push('Pregnancy trimester 2/3 TSH interval applied.');
        return {
          reference: { low: 0.2, high: 3.0, unit: 'miu/l' },
          optimal: { low: 0.4, high: 2.5, unit: 'miu/l' },
          notes
        };
      }
      return {
        reference: { low: 0.4, high: 4.5, unit: 'miu/l' },
        optimal: { low: 0.7, high: 2.5, unit: 'miu/l' },
        notes
      };
    }
    case 'fasting_glucose':
    case 'glucose': {
      const isFasting = context.fasting !== 'non_fasting';
      if (!isFasting) {
        notes.push('Non-fasting glucose interval applied.');
        return {
          reference: { low: 70, high: 140, unit: 'mg/dl' },
          optimal: { low: 80, high: 110, unit: 'mg/dl' },
          notes
        };
      }
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy fasting glucose interval applied.');
        return {
          reference: { low: 65, high: 95, unit: 'mg/dl' },
          optimal: { low: 72, high: 90, unit: 'mg/dl' },
          notes
        };
      }
      return {
        reference: { low: 70, high: 99, unit: 'mg/dl' },
        optimal: { low: 75, high: 90, unit: 'mg/dl' },
        notes
      };
    }
    case 'fasting_insulin':
    case 'insulin': {
      if (context.fasting === 'non_fasting') {
        notes.push('Non-fasting insulin interval applied.');
        return {
          reference: { low: 2, high: 25, unit: 'uiu/ml' },
          optimal: { low: 3, high: 12, unit: 'uiu/ml' },
          notes
        };
      }
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy fasting insulin interval applied.');
        return {
          reference: { low: 2, high: 20, unit: 'uiu/ml' },
          optimal: { low: 3, high: 10, unit: 'uiu/ml' },
          notes
        };
      }
      return {
        reference: { low: 2, high: 15, unit: 'uiu/ml' },
        optimal: { low: 2, high: 8, unit: 'uiu/ml' },
        notes
      };
    }
    case 'triglycerides': {
      if (context.fasting === 'non_fasting') {
        notes.push('Non-fasting triglyceride interval applied.');
        return {
          reference: { low: 0, high: 175, unit: 'mg/dl' },
          optimal: { low: 0, high: 130, unit: 'mg/dl' },
          notes
        };
      }
      return {
        reference: { low: 0, high: 150, unit: 'mg/dl' },
        optimal: { low: 0, high: 100, unit: 'mg/dl' },
        notes
      };
    }
    case 'psa': {
      if (context.sex !== 'male') {
        return {
          reference: { low: 0, high: 4.0, unit: 'ng/ml' },
          optimal: { low: 0, high: 2.5, unit: 'ng/ml' },
          notes
        };
      }
      const age = context.age ?? 50;
      let upper = 3.5;
      if (age < 50) upper = 2.5;
      else if (age >= 60 && age < 70) upper = 4.5;
      else if (age >= 70) upper = 6.5;
      notes.push('Age-adjusted PSA interval applied.');
      return {
        reference: { low: 0, high: upper, unit: 'ng/ml' },
        optimal: { low: 0, high: Math.min(2.5, upper), unit: 'ng/ml' },
        notes
      };
    }
    case 'hs_crp': {
      return {
        reference: { low: 0, high: 3, unit: 'mg/l', method: 'high sensitivity' },
        optimal: { low: 0, high: 1, unit: 'mg/l', method: 'high sensitivity' },
        notes
      };
    }
    case 'hba1c': {
      if (context.pregnancy !== 'unknown' && context.pregnancy !== 'not_pregnant') {
        notes.push('Pregnancy HbA1c interval applied.');
        return {
          reference: { low: 4.0, high: 6.0, unit: '%' },
          optimal: { low: 4.3, high: 5.3, unit: '%' },
          notes
        };
      }
      return {
        reference: { low: 4.0, high: 5.6, unit: '%' },
        optimal: { low: 4.6, high: 5.2, unit: '%' },
        notes
      };
    }
    default:
      return null;
  }
};

const matchMethod = (expected: string | undefined, actual: string | null): { match: MethodMatch; confidence: number; note?: string } => {
  if (!expected) {
    return { match: 'not_provided', confidence: 0.75 };
  }

  if (!actual) {
    return {
      match: 'not_provided',
      confidence: 0.65,
      note: `Interval expects assay method "${expected}" but result method is missing.`
    };
  }

  const normalizedExpected = expected.toLowerCase().trim();
  const normalizedActual = actual.toLowerCase().trim();
  if (normalizedActual.includes(normalizedExpected) || normalizedExpected.includes(normalizedActual)) {
    return { match: 'matched', confidence: 1 };
  }

  return {
    match: 'mismatch',
    confidence: 0.45,
    note: `Assay method mismatch (${actual} vs expected ${expected}).`
  };
};

const resolveBandFromDistance = (ratio: number, direction: 'low' | 'high'): MarkerBand => {
  if (ratio <= 0.15) return direction === 'low' ? 'slightly_low' : 'slightly_high';
  if (ratio <= 0.5) return direction === 'low' ? 'low' : 'high';
  return 'critical';
};

const classifyValue = (
  value: number,
  reference: IntervalBounds,
  optimal: IntervalBounds
): { band: MarkerBand; direction: 'low' | 'within_range' | 'high' } => {
  const refLow = reference.low;
  const refHigh = reference.high;

  const span = isFiniteNumber(refLow) && isFiniteNumber(refHigh)
    ? Math.max(refHigh - refLow, 1)
    : isFiniteNumber(refHigh)
      ? Math.max(Math.abs(refHigh) * 0.2, 1)
      : isFiniteNumber(refLow)
        ? Math.max(Math.abs(refLow) * 0.2, 1)
        : 1;

  if (isFiniteNumber(refLow) && value < refLow) {
    const ratio = (refLow - value) / span;
    return { band: resolveBandFromDistance(ratio, 'low'), direction: 'low' };
  }

  if (isFiniteNumber(refHigh) && value > refHigh) {
    const ratio = (value - refHigh) / span;
    return { band: resolveBandFromDistance(ratio, 'high'), direction: 'high' };
  }

  if (isFiniteNumber(optimal.low) && value < optimal.low) {
    return { band: 'slightly_low', direction: 'within_range' };
  }

  if (isFiniteNumber(optimal.high) && value > optimal.high) {
    return { band: 'slightly_high', direction: 'within_range' };
  }

  return { band: 'optimal', direction: 'within_range' };
};

const mapBandToLegacyStatus = (band: MarkerBand): LegacyMarkerStatus => {
  if (band === 'optimal') return 'optimal';
  if (band === 'slightly_low' || band === 'slightly_high') return 'watch';
  if (band === 'low' || band === 'high' || band === 'critical') return 'attention';
  return 'unknown';
};

const scoreFromBand = (band: MarkerBand): number => {
  switch (band) {
    case 'optimal':
      return 95;
    case 'slightly_low':
    case 'slightly_high':
      return 78;
    case 'low':
    case 'high':
      return 55;
    case 'critical':
      return 30;
    case 'reference_not_available':
    default:
      return 50;
  }
};

const summarizeRange = (
  range: IntervalBounds,
  source: IntervalSource,
  fallbackUnit: string | null,
  fallbackMethod: string | null
): MarkerRangeSummary => ({
  lower: isFiniteNumber(range.low) ? range.low : null,
  upper: isFiniteNumber(range.high) ? range.high : null,
  unit: range.unit || fallbackUnit || null,
  source,
  method: range.method || fallbackMethod || null
});

const resolveContextCoverage = (markerKey: string, context: NormalizedContext): number => {
  const relevant = MARKER_CONTEXT_RELEVANCE[markerKey] || [];
  if (relevant.length === 0) return 1;

  const provided = relevant.filter(field => {
    if (field === 'sex') return context.sex !== 'unknown';
    if (field === 'age') return isFiniteNumber(context.age);
    if (field === 'pregnancy') return context.pregnancy !== 'unknown';
    if (field === 'menopause') return context.menopause !== 'unknown';
    if (field === 'fasting') return context.fasting !== 'unknown';
    if (field === 'medications') return context.medications.length > 0;
    if (field === 'assayMethod') return !!context.assayMethod;
    return false;
  }).length;

  return clamp(provided / relevant.length, 0, 1);
};

const hasMedicationHint = (medications: string[], hints: string[]) => {
  const lowerHints = hints.map(h => h.toLowerCase());
  return medications.some(med => lowerHints.some(hint => med.includes(hint)));
};

export const evaluateMarkerReference = ({
  value,
  markerKey,
  markerConfig,
  sexOrContext,
  context
}: {
  value: number | null | undefined;
  markerKey: string;
  markerConfig?: MarkerConfigLike;
  sexOrContext?: string | number | null | MarkerEvaluationContext;
  context?: MarkerEvaluationContext;
}): MarkerEvaluationResult => {
  const key = canonicalizeMarkerKey(markerKey);

  if (!isFiniteNumber(value)) {
    return {
      status: 'unknown',
      band: 'reference_not_available',
      direction: 'unknown',
      score: 50,
      reference: { lower: null, upper: null, unit: normalizeUnit(markerConfig?.units) || null, source: 'unavailable', method: null },
      optimal: { lower: null, upper: null, unit: normalizeUnit(markerConfig?.units) || null, source: 'unavailable', method: null },
      confidence: {
        intervalSource: 'unavailable',
        unitConversionConfidence: 0,
        methodMatch: 'not_provided',
        methodMatchConfidence: 0,
        contextCoverage: 0,
        overall: 0,
        notes: ['No numeric value available for classification.']
      }
    };
  }

  const normalizedContext = normalizeContext(sexOrContext, context, markerConfig);
  const notes: string[] = [];

  const labRange = resolveLabRange(normalizedContext);
  const profiled = adjustForProfiledRules(key, normalizedContext);
  const generic = parseGenericFallback(markerConfig?.optimalRange, normalizeUnit(markerConfig?.units));

  let source: IntervalSource = 'unavailable';

  let reference: IntervalBounds = {};
  let optimal: IntervalBounds = {};

  if (labRange) {
    source = 'lab_provided';
    reference = {
      low: labRange.low,
      high: labRange.high,
      unit: labRange.unit,
      method: labRange.method
    };

    if (profiled) {
      const convertedProfiled = {
        low: profiled.optimal.low,
        high: profiled.optimal.high,
        unit: profiled.optimal.unit,
        method: profiled.optimal.method
      };
      optimal = convertedProfiled;
      notes.push(...profiled.notes);
    } else if (generic) {
      optimal = generic.optimal;
      notes.push('Fallback optimal band applied on top of lab-provided reference range.');
    } else {
      optimal = deriveOptimalFromReference(reference);
      notes.push('Optimal band inferred from lab-provided reference range.');
    }
  } else if (profiled) {
    source = 'fallback_profiled';
    reference = profiled.reference;
    optimal = profiled.optimal;
    notes.push(...profiled.notes);
  } else if (generic) {
    source = 'fallback_generic';
    reference = generic.reference;
    optimal = generic.optimal;
    notes.push('Generic fallback range inferred from configured target interval.');
  }

  if (!isFiniteNumber(reference.low) && !isFiniteNumber(reference.high)) {
    return {
      status: 'unknown',
      band: 'reference_not_available',
      direction: 'unknown',
      score: 50,
      reference: { lower: null, upper: null, unit: normalizeUnit(markerConfig?.units) || null, source: 'unavailable', method: null },
      optimal: { lower: null, upper: null, unit: normalizeUnit(markerConfig?.units) || null, source: 'unavailable', method: null },
      confidence: {
        intervalSource: 'unavailable',
        unitConversionConfidence: 0,
        methodMatch: 'not_provided',
        methodMatchConfidence: 0,
        contextCoverage: resolveContextCoverage(key, normalizedContext),
        overall: 0.2,
        notes: notes.concat('Reference interval not available for this biomarker.')
      }
    };
  }

  const targetUnit = normalizeUnit(reference.unit || markerConfig?.units || normalizedContext.measuredUnit || null);
  const measuredUnit = normalizedContext.measuredUnit || targetUnit;
  const conversion = convertValueIfNeeded(key, value, measuredUnit, targetUnit);

  if (conversion.note) notes.push(conversion.note);

  if (conversion.confidence < 0.5 && measuredUnit && targetUnit && measuredUnit !== targetUnit) {
    notes.push('Classification confidence reduced due to unsupported unit conversion.');
  }

  const convertedReferenceLow = isFiniteNumber(reference.low)
    ? convertValueIfNeeded(key, reference.low, normalizeUnit(reference.unit) || targetUnit, targetUnit).value
    : undefined;

  const convertedReferenceHigh = isFiniteNumber(reference.high)
    ? convertValueIfNeeded(key, reference.high, normalizeUnit(reference.unit) || targetUnit, targetUnit).value
    : undefined;

  const convertedOptimalLow = isFiniteNumber(optimal.low)
    ? convertValueIfNeeded(key, optimal.low, normalizeUnit(optimal.unit) || targetUnit, targetUnit).value
    : undefined;

  const convertedOptimalHigh = isFiniteNumber(optimal.high)
    ? convertValueIfNeeded(key, optimal.high, normalizeUnit(optimal.unit) || targetUnit, targetUnit).value
    : undefined;

  const alignedReference: IntervalBounds = {
    low: convertedReferenceLow,
    high: convertedReferenceHigh,
    unit: targetUnit || undefined,
    method: reference.method
  };

  const alignedOptimal: IntervalBounds = {
    low: convertedOptimalLow,
    high: convertedOptimalHigh,
    unit: targetUnit || undefined,
    method: optimal.method || reference.method
  };

  if (isFiniteNumber(alignedReference.low) && isFiniteNumber(alignedOptimal.low)) {
    alignedOptimal.low = Math.max(alignedOptimal.low, alignedReference.low);
  }
  if (isFiniteNumber(alignedReference.high) && isFiniteNumber(alignedOptimal.high)) {
    alignedOptimal.high = Math.min(alignedOptimal.high, alignedReference.high);
  }

  const methodCheck = matchMethod(reference.method || optimal.method, normalizedContext.assayMethod);
  if (methodCheck.note) notes.push(methodCheck.note);

  const classification = classifyValue(conversion.value, alignedReference, alignedOptimal);
  const band = classification.band;
  const baseScore = scoreFromBand(band);

  const sourceConfidence = source === 'lab_provided'
    ? 0.95
    : source === 'fallback_profiled'
      ? 0.82
      : source === 'fallback_generic'
        ? 0.68
        : 0.2;

  const contextCoverage = resolveContextCoverage(key, normalizedContext);

  if (normalizedContext.fasting === 'unknown' && ['glucose', 'fasting_glucose', 'insulin', 'fasting_insulin', 'triglycerides'].includes(key)) {
    notes.push('Fasting state missing; interval may be less precise.');
  }

  if (normalizedContext.pregnancy === 'unknown' && ['tsh', 'hba1c', 'ferritin', 'hemoglobin', 'hematocrit'].includes(key)) {
    notes.push('Pregnancy status missing; context-specific interval could not be applied.');
  }

  if (normalizedContext.menopause === 'unknown' && ['estradiol', 'fsh', 'ferritin'].includes(key) && normalizedContext.sex === 'female') {
    notes.push('Menopausal status missing; broad female interval used.');
  }

  if (normalizedContext.medications.length > 0) {
    const meds = normalizedContext.medications;
    if (key === 'ldl' && hasMedicationHint(meds, ['statin', 'ezetimibe', 'pcsk9'])) {
      notes.push('Lipid-lowering medication detected; interpretation should consider treatment target strategy.');
    }
    if ((key === 'tsh' || key === 'free_t4' || key === 'free_t3') && hasMedicationHint(meds, ['levothyroxine', 'liothyronine', 'thyroid'])) {
      notes.push('Thyroid medication detected; assay timing relative to dosing may influence results.');
    }
    if ((key === 'glucose' || key === 'fasting_glucose' || key === 'hba1c') && hasMedicationHint(meds, ['metformin', 'insulin', 'glp-1', 'semaglutide', 'tirzepatide', 'sulfonylurea'])) {
      notes.push('Glucose-lowering medication detected; evaluate trend and treatment goal together.');
    }
  }

  const overall = clamp(
    sourceConfidence * 0.5 + conversion.confidence * 0.25 + methodCheck.confidence * 0.15 + contextCoverage * 0.1,
    0,
    1
  );

  const confidenceAdjustedScore = Math.round(baseScore * (0.85 + overall * 0.15));

  return {
    status: mapBandToLegacyStatus(band),
    band,
    direction: classification.direction,
    score: confidenceAdjustedScore,
    reference: summarizeRange(alignedReference, source, targetUnit, reference.method || null),
    optimal: summarizeRange(alignedOptimal, source, targetUnit, optimal.method || null),
    confidence: {
      intervalSource: source,
      unitConversionConfidence: clamp(conversion.confidence, 0, 1),
      methodMatch: methodCheck.match,
      methodMatchConfidence: clamp(methodCheck.confidence, 0, 1),
      contextCoverage,
      overall,
      notes
    }
  };
};

export const isAttentionStatus = (status: string | null | undefined): boolean => status === 'attention';
